/**
 * Project History Refresh Plugin for Pinia
 *
 * This plugin automatically manages project history operations by:
 * 1. Tracking store actions that should be recorded in project history
 * 2. Distinguishing between undoable operations and history-only operations
 * 3. Refreshing the "last operation" data when undoable actions complete
 * 4. Clearing the "last operation" when history-only actions complete
 *
 * The plugin ensures the UI stays in sync with the project's operation history,
 * enabling features like undo/redo and accurate history tracking.
 */

import { PiniaPluginContext } from 'pinia';
import { useActivityProjectStore } from '@/store/activityProjectStore';
import { useHistoryStore } from '@/store/historyStore';
import { useMeetingStore } from '@/store/integrationStore/meetingStore';
import { useMilestoneProjectStore } from '@/store/milestoneProjectStore';
import { usePhaseProjectStore } from '@/store/phaseProjectStore';
import { useProjectStore } from '@/store/projectStore';
import { useRouteStore } from '@/store/routeStore';

/**
 * Pages where history operations should be tracked.
 * Only actions performed on these pages will trigger history refresh operations.
 */
const PAGE_NAMES = [
  'ProjectPage',
  'ProjectActivityDetail',
  'ProjectActivityCompletionCriteria',
  'ProjectCriterion',
  'ProjectActivityPredecessors',
  'ProjectActivitySuccessors',
  'ProjectActivityApprovals',
  'ProjectActivityWatchersPage',
  'ProjectActivityActions',
  'ProjectActivityAction',
  'ProjectActivityCommentsPage',
  'ProjectMilestoneApprovalsPage',
  'ProjectLinksAndTools',
  'ProjectSettingsDocuments',
  'ProjectSettingsFunctions',
  'ProjectSettingsMembers',
  'ProjectMeetings',
];

/**
 * Store IDs that this plugin monitors for history operations.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const storeIds = [
  useProjectStore.$id,
  useActivityProjectStore.$id,
  useMilestoneProjectStore.$id,
  usePhaseProjectStore.$id,
  useMeetingStore.$id,
] as const;

type StoreKey = (typeof storeIds)[number];

/**
 * Classification of store actions based on their history behavior:
 * - 'history-only': Action is recorded in history but cannot be undone
 * - 'undoable': Action is recorded in history and can be undone
 */
type ActionType = 'history-only' | 'undoable';

/**
 * Maps store actions to their history behavior classification.
 *
 * Each store action is classified as either:
 * - 'history-only': Tracked in history but not undoable (e.g., detail updates, bulk operations)
 * - 'undoable': Tracked in history and can be undone (e.g., create, update, delete operations)
 *
 * This configuration determines how the plugin responds to each action:
 * - Undoable actions: Refresh the last operation data after completion
 * - History-only actions: Clear the last operation data after completion
 */
const storeActionMap: Record<StoreKey, Record<string, ActionType>> = {
  projectStore: {
    updateDetails: 'history-only', // updateProjectDetails
    update: 'undoable',
    applySpaceTool: 'undoable', // shiftProjectElements
    /* Action */
    addAction: 'undoable', // addAction <-- move to activity store
    removeAction: 'undoable', // <- move to activity store,
    updateAction: 'undoable', // activityUpdateAction <-- move to activity store
    /* Completion Criterion */
    addCompletionCriterion: 'undoable', // addCompletionCriteria <- move to activity store
    updateCompletionCriterion: 'undoable', // activityUpdateCompletionCriterion <- move to activity store,
    removeCompletionCriterion: 'undoable', // <- move to activity store,
    /* Workstream */
    updateWorkstream: 'undoable',
    addWorkstream: 'undoable',
    deleteWorkstream: 'undoable',
    /* Function */
    addFunction: 'undoable',
    updateProjFunction: 'undoable',
    /* Link */
    createLink: 'undoable', // addLinkToProject
    updateLink: 'undoable', // updateLink
    deleteLink: 'undoable', // removeLink
    /* Document */
    addDocument: 'undoable', // addDocumentToProject
    updateDocument: 'undoable', // updateDocument
    removeDocument: 'undoable', // removeDocument
    /* Member */
    addMembers: 'undoable',
    removeMember: 'undoable',
  },
  activityProjectStore: {
    create: 'undoable', // addPhaseActivity
    update: 'undoable', // updateProjectsActivity
    remove: 'undoable', // deleteProjectsActivity
    toggleFavorite: 'undoable', // toggleActivityFavorite
    updateActivitiesFavorite: 'undoable', // bulk update favorite state
    // removeActivities: 'history-only', // deleteProjectActivities
    removeActivities: 'undoable', // deleteProjectActivities
    updateActivities: 'undoable', // updateProjectActivities
    updatePosition: 'undoable', // updateProjectsActivity
    updatePositions: 'undoable',
    /* Predecessor/Successor */
    addPredecessor: 'undoable',
    addSuccessor: 'undoable',
    removePredecessor: 'undoable',
    removeSuccessor: 'undoable',
    /* Watcher */
    addWatcher: 'undoable', // addWatcher
    removeWatcher: 'undoable',
    /* Approver */
    addApproval: 'undoable', // addApproval
    updateApprover: 'undoable', // updateActivityApprover
    removeApprover: 'undoable', // removeActivityApprover
    /* Action */
    removeAction: 'undoable', // activityRemoveAction
    convertActionToActivity: 'undoable',
    /* Completion Criterion */
    convertCompletionCriterionToActivity: 'undoable',
    removeCompletionCriterion: 'undoable', // activityRemoveCompletionCriterion
    /* Tag */
    addTag: 'undoable',
    removeTag: 'undoable',
    updateActivitiesTags: 'undoable',
    /* Comment */
    addActivityComment: 'undoable',
    updateActivityComment: 'undoable',
    removeActivityComment: 'undoable',
    addActionComment: 'undoable',
    updateActionComment: 'undoable',
    removeActionComment: 'undoable',
    addCompletionCriterionComment: 'undoable',
    updateCompletionCriterionComment: 'undoable',
    removeCompletionCriterionComment: 'undoable',
  },
  milestoneProjectStore: {
    update: 'undoable', // updateProjectMilestone
    updateDeadline: 'undoable', // updateProjectMilestone
    createMilestone: 'undoable',
    remove: 'undoable',
    /* Approval */
    addApprover: 'undoable',
    removeApprover: 'undoable',
    updateApprover: 'undoable',
    /* Comment */
    addMilestoneComment: 'undoable',
    updateMilestoneComment: 'undoable',
    removeMilestoneComment: 'undoable',
  },
  phaseProjectStore: {
    update: 'undoable', // updateProjectPhase
    add: 'undoable',
    remove: 'undoable',
    applyCompressTool: 'undoable',
    applySpaceTool: 'undoable', // shiftPhaseElements
  },
  meetingIntegrationStore: {
    saveEntityReferences: 'undoable',
    deleteEntityReference: 'undoable',
  },
};

/**
 * Sets up action listeners for stores to handle history refresh operations.
 *
 * For each monitored store action:
 * 1. Checks if the current page should track history operations
 * 2. Determines the action's history behavior classification
 * 3. After action completion:
 *    - Undoable actions: Fetches latest history operation (enables undo button)
 *    - History-only actions: Clears last operation (disables undo button)
 */
function setupActionListeners(store: PiniaPluginContext['store']) {
  const routeStore = useRouteStore();
  const historyStore = useHistoryStore();
  const fetchLastOperation = (projectId: string) =>
    historyStore.fetchLastHistoryOperation(projectId);

  store.$onAction(({ name, after }) => {
    const projectId = routeStore.params.projectId as string;

    // Only track history operations on relevant project pages
    if (
      typeof routeStore.pageName !== 'string' ||
      !PAGE_NAMES.includes(routeStore.pageName)
    ) {
      return;
    }

    // Get the action's history behavior classification
    const actionConfig = storeActionMap[store.$id as StoreKey];
    const actionType = actionConfig?.[name];

    // Handle post-action history refresh based on action type
    if (actionType === 'undoable') {
      // Undoable actions: refresh last operation to enable undo functionality
      after(() => fetchLastOperation(projectId));
    } else if (actionType === 'history-only') {
      // History-only actions: clear last operation to disable undo functionality
      // In other words: disable undo button
      after(() => historyStore.clearLastHistoryOperation(projectId));
    }
  });
}

/**
 * Pinia plugin that automatically manages project history refresh operations.
 *
 * This plugin attaches to relevant stores and:
 * 1. Monitors store actions that affect project history
 * 2. Refreshes or clears the "last operation" data after actions complete
 * 3. Ensures undo/redo functionality stays synchronized with operation history
 *
 * Special handling for historyStore:
 * - The 'undoOperation' action refreshes last operation data after completion
 *
 * For other monitored stores (project, activity, milestone, phase):
 * - Uses the generic action listener setup with classification-based behavior
 */
export function projectHistoryRefreshPlugin({ store }: PiniaPluginContext) {
  switch (store.$id) {
    case useHistoryStore.$id:
      // Special handling for undo operations in the history store
      store.$onAction(({ name, after }) => {
        const routeStore = useRouteStore();
        const historyStore = useHistoryStore();
        const projectId = routeStore.params.projectId as string;

        // Only process on relevant project pages
        if (
          typeof routeStore.pageName !== 'string' ||
          !PAGE_NAMES.includes(routeStore.pageName)
        ) {
          return;
        }

        // After undo operation, refresh the last operation data
        if (name === 'undoOperation') {
          after(() => historyStore.fetchLastHistoryOperation(projectId));
        }
      });
      break;

    case useProjectStore.$id:
    case useActivityProjectStore.$id:
    case useMilestoneProjectStore.$id:
    case usePhaseProjectStore.$id:
    case useMeetingStore.$id:
      // Use generic action listener setup for project-related stores
      setupActionListeners(store);
      break;
  }
}
